import { useEffect, useState } from "react";
import { PATHS } from "../../config/permissionsPaths";
import useAuth from "../../hooks/useAuth";
import { FaSearch, FaLink } from "react-icons/fa";
import { BsUpload } from "react-icons/bs";
import { BtnGeneral } from "../../styles/Buttons";
import EnlacesModal from "../../components/Modals/EnlacesModal";
import useEnlaces from "../../hooks/enlaces/useEnlaces";
import Accordion from "../../components/Common/Accordion";
import {
  Sidebar,
  MainContent,
  SearchBar,
  Input,
  CardsWrapper,
  Card,
  Title,
  Metadata,
} from "./Enlaces.styles";

export default function Enlaces() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hook = useEnlaces();

  const {
    categorias,
    loadCategorias,
    keyword,
    setKeyword,
    documentos,
    fetchDocumentos,
    tiposSeleccionados,
    toggleTipo,
    isGroupSelected,
    handleParentToggle,
  } = hook;

  const { hasPermission } = useAuth();

  useEffect(() => {
    loadCategorias();
  }, []);

  useEffect(() => {
    fetchDocumentos();
  }, [keyword, tiposSeleccionados]);

  return (
    <div className="div-container">
      {/* ── SIDEBAR FILTROS ─────────────────────────────── */}
      <Sidebar>
        <h3 style={{ margin: "0 0 12px 0", fontSize: "13px", fontWeight: "700", color: "#1e293b", fontFamily: "var(--font-body)" }}>
          Filtrar por categoría
        </h3>

        {Object.entries(categorias).map(([categoria, subtipos]) => (
          <Accordion
            key={categoria}
            title={categoria}
            count={subtipos.filter((s) => tiposSeleccionados.includes(s.id)).length || undefined}
            defaultOpen={false}
            fontSize="11px"
            headerPadding="6px 10px"
            bodyPadding="4px 8px 6px"
            style={{ marginBottom: "4px" }}
          >
            {/* Checkbox padre — seleccionar todo el grupo */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                fontFamily: "var(--font-body)",
                color: "#475569",
                padding: "4px 2px",
                cursor: "pointer",
                fontWeight: "600",
                borderBottom: "1px solid #f1f5f9",
                marginBottom: "2px",
              }}
            >
              <input
                type="checkbox"
                checked={isGroupSelected(subtipos)}
                onChange={() => handleParentToggle(subtipos)}
              />
              Todos
            </label>

            {subtipos.map((sub) => (
              <label
                key={sub.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                  padding: "3px 2px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  transition: "background 0.12s",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <input
                    type="checkbox"
                    checked={tiposSeleccionados.includes(sub.id)}
                    onChange={() => toggleTipo(sub.id)}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      fontFamily: "var(--font-body)",
                      color: tiposSeleccionados.includes(sub.id) ? "#1e40af" : "#334155",
                      fontWeight: tiposSeleccionados.includes(sub.id) ? "600" : "400",
                    }}
                  >
                    {sub.nombre}
                  </span>
                </span>
                {sub.detalle && (
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#94a3b8",
                      fontFamily: "var(--font-body)",
                      paddingLeft: "20px",
                      lineHeight: "1.3",
                    }}
                  >
                    {sub.detalle}
                  </span>
                )}
              </label>
            ))}
          </Accordion>
        ))}
      </Sidebar>

      {/* ── CONTENIDO PRINCIPAL ─────────────────────────── */}
      <MainContent>
        <SearchBar>
          <Input
            placeholder="Buscar por palabra clave..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <BtnGeneral bg="blue" padding="12px" radius="0 8px 8px 0" flex="0">
            <FaSearch />
          </BtnGeneral>

          {hasPermission(PATHS.CARGAR_ENLACES) && (
            <BtnGeneral bg="green" mgl="10px" onClick={() => setIsModalOpen(true)}>
              <BsUpload style={{ marginRight: "6px" }} /> Cargar URL
            </BtnGeneral>
          )}
        </SearchBar>

        <CardsWrapper>
          {documentos.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: "13px", fontFamily: "var(--font-body)" }}>
              No se encontraron enlaces
            </p>
          ) : (
            documentos.map((doc) => (
              <Card key={doc.id}>
                <Title>{doc.titulo}</Title>
                <Metadata>Fecha: {new Date(doc.fecha_creacion).toLocaleDateString()}</Metadata>
                <Metadata>
                  {doc.tipoDescripcion}
                  {doc.subtipoDescripcion && ` › ${doc.subtipoDescripcion}`}
                </Metadata>
                {doc.subtipoDetalle && (
                  <Metadata style={{ color: "#94a3b8", fontSize: "11px" }}>
                    {doc.subtipoDetalle}
                  </Metadata>
                )}
                {doc.palabras_clave && (
                  <Metadata style={{ fontSize: "11px" }}>
                    {doc.palabras_clave.split(",").map((kw, i) => (
                      <span
                        key={i}
                        style={{
                          display: "inline-block",
                          background: "#dbeafe",
                          color: "#1e40af",
                          borderRadius: "10px",
                          padding: "1px 7px",
                          fontSize: "10px",
                          marginRight: "4px",
                          marginBottom: "3px",
                          fontFamily: "var(--font-body)",
                        }}
                      >
                        {kw.trim()}
                      </span>
                    ))}
                  </Metadata>
                )}

                {hasPermission(PATHS.VER_ENLACES) && (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#0077b6",
                      fontWeight: "bold",
                      textDecoration: "underline",
                      marginTop: "auto",
                      paddingTop: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    <FaLink /> Abrir enlace
                  </a>
                )}
              </Card>
            ))
          )}
        </CardsWrapper>

        <EnlacesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          hook={hook}
          onSaved={fetchDocumentos}
        />
      </MainContent>
    </div>
  );
}
